import React, { useState, useEffect } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import LoadingAnimation from "../../Components/Loading/LoadingAnimation";
import {
  FETCH_PERMISSION_URL_BY_ROLEID,
  CREATE_OR_UPDATE_ROLE_URL,
} from "../../Constants/apiRoutes";
import { toast, ToastContainer } from "react-toastify";
import { Combobox } from "@headlessui/react";
import { ChevronUpDownIcon } from "@heroicons/react/20/solid";
const functions = [
  { id: 1, name: "Technical Function" },
  { id: 2, name: "Finance" },
  { id: 3, name: "Human Resources" },
  { id: 4, name: "Marketing" },
  { id: 5, name: "Sales" },
  { id: 6, name: "Projects" },
  { id: 7, name: "Legal" },
  { id: 8, name: "Management" },
  { id: 9, name: "Others" },
];
const EditRoleForm = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    roleId,
    roleName,
    company,
    DepartmentIds = [],
    businessUnitName,
    Status,
  } = location.state || {};

  const [updatedRoleName, setUpdatedRoleName] = useState(roleName);
  const [permissionsByModule, setPermissionsByModule] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [companyName, setCompanyName] = useState(company);
  const [status, setStatus] = useState(Status);
const isAdminRole = updatedRoleName.toLowerCase() === "admin";
  // const [selectedFunctions, setSelectedFunctions] = useState([DepartmentIds]);

  const [query, setQuery] = useState("");
  const [businessUnit, setBusinessUnit] = useState(businessUnitName);
  const [selectedFunctions, setSelectedFunctions] = useState(() =>
    functions.filter((func) => DepartmentIds.includes(func.id))
  );

  const filteredFunctions = query
    ? functions.filter((func) =>
        func.name.toLowerCase().includes(query.toLowerCase())
      )
    : functions;

  const handleFunctionChange = (func) => {
    const exists = selectedFunctions.some((f) => f.id === func.id);
    if (exists) {
      setSelectedFunctions((prev) => prev.filter((f) => f.id !== func.id));
    } else {
      setSelectedFunctions((prev) => [...prev, func]);
    }
  };

  const handleSelectAll = () => {
    const allSelected = selectedFunctions.length === filteredFunctions.length;
    setSelectedFunctions(allSelected ? [] : filteredFunctions);
  };

  // Fetch role permissions and categorize them by PermissionModule
  useEffect(() => {
    const fetchRolePermissions = async () => {
      setLoading(true); // Start loading animation when API call starts
      const token = localStorage.getItem("token");
      try {
        const response = await axios.get(
          `${FETCH_PERMISSION_URL_BY_ROLEID}/${roleId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const data = response.data;
        const categorizedPermissions = {};

        if (data && Array.isArray(data)) {
          data.forEach((permission) => {
            const module = permission.PermissionModule; // Use PermissionModule from the response
            if (!categorizedPermissions[module]) {
              categorizedPermissions[module] = [];
            }
            categorizedPermissions[module].push({
              ID: permission.PermissionId,
              Name: permission.PermissionName,
              IsChecked: permission.IsChecked,
            });
          });
        }
        setPermissionsByModule(categorizedPermissions);
      } catch (err) {
        setError("Failed to fetch role permissions");
      } finally {
        setLoading(false); // Stop loading animation when API call is finished
      }
    };

    fetchRolePermissions();
  }, [roleId]);

  const handleClose = () => {
    navigate("/RoleUser");
  };

  const handleCheckboxChange = (moduleName, permissionId) => {
    setPermissionsByModule((prevState) => {
      const updatedPermissions = { ...prevState };
      updatedPermissions[moduleName] = updatedPermissions[moduleName].map(
        (permission) =>
          permission.ID === permissionId
            ? { ...permission, IsChecked: !permission.IsChecked }
            : permission
      );
      return updatedPermissions;
    });
  };

  const handleSelectAllChange = (moduleName, isChecked) => {
    setPermissionsByModule((prevState) => {
      const updatedPermissions = { ...prevState };
      updatedPermissions[moduleName] = updatedPermissions[moduleName].map(
        (permission) => ({
          ...permission,
          IsChecked: isChecked,
        })
      );
      return updatedPermissions;
    });
  };
  const handleSubmit = async (event) => {
    event.preventDefault();
    const updatedPermissions = [];
    const DepartmentIds = selectedFunctions.map((f) => f.id);

    const payload = {
      RoleID: roleId || 0,
      TenantID: parseInt(localStorage.getItem("TenantID")) || 0,
      RoleName: updatedRoleName,
      CompanyName: companyName || "",
      DepartmentIds: DepartmentIds,
      Status: status,
      BusinessUnit: businessUnit || "",
      permissions: updatedPermissions,
    };

    // Validation
    const validateRoleDataSubmit = () => {
      if (!payload.RoleName) return "Role Name is required.";
      if (!payload.CompanyName) return "Company Name is required.";
      if (payload.DepartmentIds.length === 0)
        return "At least one department is required.";
      return null;
    };

    const validationError = validateRoleDataSubmit();
    if (validationError) {
      toast.error(validationError);
      return;
    }

    // Add permissions
    Object.keys(permissionsByModule).forEach((moduleName) => {
      permissionsByModule[moduleName].forEach((permission) => {
        updatedPermissions.push({
          permissionId: permission.ID,
          isChecked: permission.IsChecked,
        });
      });
    });

    try {
      setIsLoading(true);
      const token = localStorage.getItem("token");

      const response = await axios.post(
        `${CREATE_OR_UPDATE_ROLE_URL}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      toast.success("User Role updated successfully!");
      setTimeout(() => {
        navigate("/Roleuser");
      }, 1000);
    } catch (error) {
      console.error(error);
      const errorMessage =
    error?.response?.data?.message || "Error saving role. Please try again.";
  toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Show loading animation when page is loading or form is submitting
  if (loading || isLoading) return <LoadingAnimation />;
  if (error) return <div>{error}</div>;
  
const isDisabled = isAdminRole;
const getInputClass = (isDisabled) =>
  `border border-gray-300 p-2 w-full sm:w-[70%] rounded-md ${
    isDisabled ? "bg-gray-100 cursor-not-allowed" : ""
  }`;


  return (
    <div className={`main-container`}>
      <div className="p-6 rounded-lg ">
        <ToastContainer />
        {/* <h2 className="text-xl font-semibold mb-6"> */}
        <div className="flex items-center justify-between w-full mb-4">
          <h2 className="text-lg font-bold text-[#8B4513]">Edit Role</h2>
          <div className="flex items-center space-x-2">
            <label className="font-semibold">Is Active:</label>
            <div
              onClick={() =>
    !isAdminRole &&
    setStatus((prev) => (prev === "Active" ? "Inactive" : "Active"))
  }
   disabled={isAdminRole}
  className={`relative w-14 h-6 rounded-full transition cursor-pointer ${
    status === "Active" ? "bg-green-500" : "bg-red-500"
  } ${isAdminRole ? "opacity-50 cursor-not-allowed pointer-events-none" : ""}`}

            >
              <div
                className={`absolute top-1/2 left-1 transform -translate-y-1/2 w-5 h-5 bg-white rounded-full shadow-md transition-transform ${
                  status === "Active" ? "translate-x-8" : "translate-x-0"
                }`}
              ></div>
            </div>
          </div>
        </div>

        <hr className="border-[#8B4513] opacity-20 my-4 mt-2 mb-4" />

        <div className="mb-4 flex flex-col items-center justify-center w-full"></div>

        <div className="mb-4 flex flex-col sm:flex-row items-center justify-center w-full">
          {/* Role Name Field */}
          <div className="flex flex-col sm:flex-row items-center w-full sm:w-3/4">
            <label className="block font-semibold mr-[14px] min-w-[150px] text-right whitespace-nowrap">
              Role Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={updatedRoleName}
              onChange={(e) => setUpdatedRoleName(e.target.value)} 
   className={getInputClass(isDisabled)}
              placeholder="Enter Role Name"
            />
          </div>

          {/* Company Name Field */}
          <div className="flex flex-col sm:flex-row items-center w-full sm:w-3/4">
            <label className="block font-semibold mr-[14px] min-w-[150px] text-right whitespace-nowrap">
              Company Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="companyName"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
  className={getInputClass(isDisabled)}
              placeholder="Enter Company Name"
            />
          </div>
        </div>
        <div className="mb-4 flex flex-col sm:flex-row items-center justify-center w-full">
          <div className="flex flex-col sm:flex-row items-center w-full sm:w-1/2">
            <label className="block font-semibold mr-[14px] min-w-[150px] text-right whitespace-nowrap">
              Function <span className="text-red-500">*</span>
            </label>
            <div className="w-full sm:w-[70%]">
              <Combobox
                as="div"
                value={selectedFunctions}
                onChange={handleFunctionChange}
                multiple
                className="w-full"
              >
                <div className="relative w-full">
                  <Combobox.Input
                    id="FunctionID"
                    name="FunctionID"
                     disabled={isAdminRole}
                  
                    className={`block w-full border border-gray-300 p-3 rounded-md shadow-sm sm:text-sm focus:outline-none ${
    isAdminRole ? "bg-gray-100 cursor-not-allowed" : ""
  }`}
                    displayValue={() =>
                      selectedFunctions.map((f) => f.name).join(", ") || ""
                    }
                    placeholder="Select Function"
                    readOnly
                  />
                  <Combobox.Button className="absolute inset-y-0 right-0 flex items-center px-2">
                    <ChevronUpDownIcon
                      className="h-5 w-5 text-gray-400"
                      aria-hidden="true"
                    />
                  </Combobox.Button>

                  <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                    {/* ✅ Select All Option */}
                    <Combobox.Option
                      key="select-all"
                      value="select-all"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSelectAll();
                      }}
                      className="group relative cursor-pointer select-none py-2 pl-3 pr-9 hover:bg-indigo-100 text-gray-900"
                    >
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={
                            selectedFunctions.length ===
                            filteredFunctions.length
                          }
                          readOnly
                          className="mr-2 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                        />
                        <span className="block truncate">Select All</span>
                      </div>
                    </Combobox.Option>

                    {/* 🔁 List of options */}
                    {filteredFunctions.map((func) => {
                      const isSelected = selectedFunctions.some(
                        (item) => item?.id === func.id
                      );
                      return (
                        <Combobox.Option
                          key={func.id}
                          value={func}
                          onClick={() => handleFunctionChange(func)}
                          className={`group relative cursor-pointer select-none py-2 pl-3 pr-9 ${
                            isSelected
                              ? "bg-indigo-600 text-white"
                              : "text-gray-900 hover:bg-indigo-100"
                          }`}
                        >
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              readOnly
                              className="mr-2 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                            />
                            <span className="block truncate">{func.name}</span>
                          </div>
                        </Combobox.Option>
                      );
                    })}
                  </Combobox.Options>
                </div>
              </Combobox>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row items-center w-full sm:w-1/2">
            <label className="block font-semibold mr-[14px] min-w-[150px] text-right whitespace-nowrap">
              Business Unit <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="businessUnit"
              value={businessUnit}
              onChange={(e) => setBusinessUnit(e.target.value)}
               className={getInputClass(isDisabled)}

              placeholder="Enter Business Unit"
            />
          </div>
        </div>
        <hr className="border-[#8B4513] opacity-20 my-4 mb-6" />

        {/* Permissions by Module */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {Object.keys(permissionsByModule).map((moduleName) => {
            const isAllSelected = permissionsByModule[moduleName].every(
              (permission) => permission.IsChecked
            );
            return (
              <div
                key={moduleName}
                className="border-[0.5px] border-[#ce8c5d] p-4 rounded-lg bg-white shadow-md"
              >
                <div className="flex justify-between items-center">
                  <h2 className="text-lg font-bold text-[#8B4513]">
                    {moduleName}
                  </h2>
                  <label className="text-sm">
                    <input
                      type="checkbox"
                      checked={isAllSelected}
                     
                      onChange={(e) =>
    !isAdminRole && handleSelectAllChange(moduleName, e.target.checked)
  }
  disabled={isAdminRole}
                      className="mr-2 form-checkbox h-[12px] w-[12px] text-[#8B4513] border-[#8B4513]"
                   
                    />
                    Select All
                  </label>
                </div>
                <hr className="border-gray-300 my-4 mt-2 mb-4" />

                {permissionsByModule[moduleName].map((permission) => (
                  <div key={permission.ID} className="flex items-center mb-2">
                    <label>
                      <input
                        type="checkbox"
                        checked={permission.IsChecked}
                     
                        className="mr-2 form-checkbox h-[12px] w-[12px] text-[#8B4513] border-[#8B4513]"
                      
                      onChange={() =>
    !isAdminRole && handleCheckboxChange(moduleName, permission.ID)
  }
  disabled={isAdminRole}
                      />
                      {permission.Name}
                    </label>
                  </div>
                ))}
              </div>
            );
          })}
        </div>

        <div className="mt-10 flex flex-col sm:flex-row justify-end space-x-0 sm:space-x-4">
          <button
            onClick={handleClose}
            className="flex items-center justify-center px-4 py-2 bg-gradient-to-br from-[#8B4513] to-[#D2691E] text-white rounded-lg hover:from-[#A0522D] hover:to-[#D2691E] transition-all duration-300 w-full sm:w-auto"
          >
            Close
          </button>

          <button
            onClick={handleSubmit}
              disabled={isAdminRole}
  className={`flex items-center justify-center px-4 py-2 bg-gradient-to-br from-[#8B4513] to-[#D2691E] text-white rounded-lg transition-all duration-300 w-full sm:w-auto ${
    isAdminRole ? "opacity-50 cursor-not-allowed" : "hover:from-[#A0522D] hover:to-[#D2691E]"
  }`}
          >
            Update Role
          </button>
          {isLoading && <LoadingAnimation />}
        </div>
      </div>
    </div>
  );
};

export default EditRoleForm;
